import { useMemo, useState } from "react";
import { Link } from "react-router";
import { Eye } from "lucide-react";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import { BotaoAdicionar } from "./BotaoAdicionar";
import { AdicionarDaBaseDialog } from "@/components/catalogo/base/AdicionarDaBaseDialog";
import { CatalogItemRow } from "./CatalogItemRow";
import { CatalogoBusca } from "./CatalogoBusca";
import { ServiceCategoryDialog, type ServiceCategoryEditTarget } from "./ServiceCategoryDialog";
import { BotaoFichas, ListaFichas, type FichaResumo } from "./ServicoFichas";
import { ServicosPorCategoria } from "./ServicosPorCategoria";
import { useLigarServicos } from "./use-ligar-servicos";
import { ehProprio, useServicosAVista } from "./use-servicos-a-vista";

type DialogState =
  | { mode: "closed" }
  | { mode: "create" }
  | {
      mode: "edit";
      target: ServiceCategoryEditTarget;
      aba?: "dados" | "ficha";
      /** Qual ficha abrir — quem clicou numa da lista quer aquela, não a primeira do serviço. */
      ficha?: { id: string; materialOptionId: string | null };
    };

const cardCls = "rounded-xl border border-zinc-200 bg-white p-5 shadow-sm dark:border-border dark:bg-card";
const sectionTitleCls = "text-sm font-semibold text-zinc-900 dark:text-foreground";

export function ServiceCategorySection() {
  const utils = trpc.useUtils();
  const listQuery = trpc.prosthesis.catalog.myServiceCategories.useQuery();
  const fichasQuery = trpc.fichaTecnica.resumo.useQuery();
  const [dialogState, setDialogState] = useState<DialogState>({ mode: "closed" });
  const [busca, setBusca] = useState("");
  const [daBase, setDaBase] = useState(false);
  const [abertos, setAbertos] = useState<Set<string>>(new Set());
  const { ligarServico, ligarCategoria, ocupado } = useLigarServicos();

  const remove = trpc.prosthesis.catalog.deleteCustomServiceCategory.useMutation({
    onSuccess: () => {
      toast.success("Serviço excluído.");
      utils.prosthesis.catalog.myServiceCategories.invalidate();
      utils.prosthesis.catalog.serviceCategories.invalidate();
      utils.fichaTecnica.resumo.invalidate();
      utils.baseDoLab.listar.invalidate();
    },
    onError: (e) => toast.error(e.message || "Erro ao excluir serviço."),
  });

  // Uma consulta só traz as fichas de todos os serviços; aqui elas voltam a ficar por serviço.
  const fichasPorServico = useMemo(() => {
    const mapa = new Map<string, FichaResumo[]>();
    for (const f of (fichasQuery.data ?? []) as FichaResumo[]) {
      if (!mapa.has(f.serviceCategoryId)) mapa.set(f.serviceCategoryId, []);
      mapa.get(f.serviceCategoryId)!.push(f);
    }
    return mapa;
  }, [fichasQuery.data]);

  const todos = useMemo(() => (listQuery.data ?? []) as any[], [listQuery.data]);
  const { filtrados, temAtivo, propriosDesligados, verProprios, alternarProprios, desmarcar } = useServicosAVista(todos, busca, fichasPorServico);
  const nenhumAtivo = listQuery.isSuccess && !temAtivo;

  const alternar = (id: string) =>
    setAbertos((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <div id="servicos" className={cardCls}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 data-tour="catalogo-gerenciar-secao-titulo" className={sectionTitleCls}>Serviços</h2>
          <p className="mt-0.5 text-xs text-zinc-500 dark:text-muted-foreground">
            O catálogo do seu laboratório — só os ativos aparecem pros dentistas ao montar um pedido.
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
          {/* Também aqui, não só na entrada do Catálogo: quem ajusta os serviços quer conferir o resultado (pedido do Jun). */}
          <Link
            to="/lab/catalogo/visualizar"
            className="inline-flex h-8 items-center gap-1.5 rounded-md border border-zinc-200 bg-white px-3 text-xs font-semibold text-brand-700 shadow-sm hover:bg-zinc-50 dark:border-border dark:bg-card dark:text-primary"
          >
            <Eye className="h-3.5 w-3.5" />
            Visualizar catálogo
          </Link>
          <BotaoAdicionar topo onClick={() => setDaBase(true)}>
            Adicionar serviço
          </BotaoAdicionar>
        </div>
        <AdicionarDaBaseDialog tipo="servicos" open={daBase} onClose={() => setDaBase(false)} onCriarNovo={() => setDialogState({ mode: "create" })} />
      </div>

      {nenhumAtivo && (
        <p className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300">
          Nenhum serviço ativo ainda. Em Adicionar serviço, escolha da base os que o laboratório faz (ou crie um
          novo) — só eles aparecem pros dentistas.
        </p>
      )}

      <CatalogoBusca
        valor={busca}
        onChange={setBusca}
        placeholder="Buscar serviço ou ficha técnica"
        achados={filtrados.length}
      />

      <div className="mt-3">
        <ServicosPorCategoria
          servicos={filtrados}
          vazio={
            busca.trim() ? (
              <p className="px-2 py-1.5 text-xs text-zinc-500 dark:text-muted-foreground">
                Nenhum serviço do catálogo com essa busca.{" "}
                <button type="button" onClick={() => setDaBase(true)} className="font-semibold text-brand-700 hover:underline dark:text-primary">
                  Procurar na base
                </button>
              </p>
            ) : null
          }
          ocupado={ocupado || remove.isPending}
          onToggleCategoria={(categoria, ligar, itens) => {
            if (ligar) itens.filter((s) => !s.active).forEach((s) => ligarServico(s.id, true));
            else {
              desmarcar(itens.map((s) => s.id));
              ligarCategoria(categoria, false);
            }
          }}
          umPorLinha
          renderItem={(s) => {
            const fichas = fichasPorServico.get(s.id) ?? [];
            const aberto = abertos.has(s.id);
            return (
              <div key={s.id}>
                <CatalogItemRow
                  label={s.label}
                  proprio={ehProprio(s)}
                  enabled={s.active}
                  disabled={ocupado || remove.isPending}
                  onToggle={(enabled) => {
                    if (!enabled) desmarcar([s.id]);
                    ligarServico(s.id, enabled);
                  }}
                  onEdit={() => setDialogState({ mode: "edit", target: s })}
                  onDelete={() => {
                    if (window.confirm(`Excluir o serviço "${s.label}"?`)) remove.mutate({ serviceCategoryId: s.id });
                  }}
                  extra={<BotaoFichas quantas={fichas.length} aberto={aberto} onAlternar={() => alternar(s.id)} label={s.label} />}
                />
                {aberto && (
                  <ListaFichas
                    fichas={fichas}
                    onAbrir={(f) =>
                      setDialogState({
                        mode: "edit",
                        target: s,
                        aba: "ficha",
                        ficha: { id: f.id, materialOptionId: f.materialOptionId },
                      })
                    }
                  />
                )}
              </div>
            );
          }}
        />
        {propriosDesligados > 0 && !busca.trim() && (
          <button type="button" onClick={alternarProprios} className="mt-2 text-xs font-semibold text-brand-700 hover:underline dark:text-primary">
            {verProprios ? "Esconder os criados por você e desligados" : `Mostrar os criados por você e desligados (${propriosDesligados})`}
          </button>
        )}
      </div>

      <ServiceCategoryDialog
        open={dialogState.mode !== "closed"}
        editTarget={dialogState.mode === "edit" ? dialogState.target : null}
        abaInicial={dialogState.mode === "edit" ? dialogState.aba : undefined}
        fichaInicial={dialogState.mode === "edit" ? dialogState.ficha : undefined}
        onClose={() => setDialogState({ mode: "closed" })}
      />
    </div>
  );
}
